import { equals } from 'ramda';
import type { NodeEntry } from 'slate';
import { Editor, Element, Node, Path, Point } from 'slate';

import type { Length } from '../../types';
import {
  arrayEntries,
  assertNeverSafely,
  convertUnit,
  inToPx,
  mapObjectValues,
  sumBy,
} from '../../util';
import type {
  LinePointPosition,
  Rect,
  RenderModelContentBlock,
  RenderModelDescendant,
  RenderModelElement,
  RenderModelLineChild,
  RenderModelNode,
  RenderModelPage,
  RenderModelParagraph,
  RenderModelTable,
  RenderModelTableCell,
  RenderModelTableRow,
  RenderModelText,
  SelectionAffinity,
} from '../internal';
import {
  FooterType,
  HeaderType,
  isAlineInlineElement,
  isBlockInstructionFieldElement,
  isRenderModelContentBlock,
  isRenderModelElement,
  isRenderModelElementEntry,
  isRenderModelLine,
  isRenderModelLineChild,
  isRenderModelPage,
  isRenderModelParagraph,
  isRenderModelTable,
  isRenderModelTableCell,
  isRenderModelTableRow,
  isRenderModelTextEntry,
  isSignatureFieldElement,
  ListLevelAlignment,
  Queries,
} from '../internal';

/**
 * These queries control the bounding boxes for render model elements and the use of these bounding
 * boxes to resolve points for clicks and other pointer events.
 *
 * To adjust the bounding box of an element, add overrides as necessary to the following queries:
 * - getRMContentOffsetPx (if the element adds padding to its children)
 * - getRMSelfOffsetPx (if the element adds margin to itself)
 * - getRMNodeWidthPx (only required if the render model element's widthPx is absent or incorrect)
 * - getRMNodeHeightPx (only required if the render model element's heightPx is absent or incorrect)
 * - getRMChildrenDirection (only required if the element's children are arranged horizontally)
 */

/**
 * A bounding box relative to the top-left corner of its parent's bounding box.
 */
export interface BaseBoundingBox extends Rect {
  /**
   * Whether this bounding box should only be considered hit if the pointer is directly inside it.
   * By default, bounding boxes can be hit when they are the closest bounding box to the pointer.
   */
  directHitOnly?: true;
}

export interface NodeBoundingBox<
  T extends RenderModelDescendant = RenderModelDescendant,
> extends BaseBoundingBox {
  rMNodeEntry: NodeEntry<T>;
  type: 'node';
}

export interface ListMarkerBoundingBox extends BaseBoundingBox {
  directHitOnly: true;
  rMParagraphEntry: NodeEntry<RenderModelParagraph>;
  type: 'list-marker';
}

export interface EnsureParagraphAtEndBoundingBox extends BaseBoundingBox {
  directHitOnly: true;
  type: 'ensure-paragraph-at-end';
}

export type SpecialBoundingBox = ListMarkerBoundingBox | EnsureParagraphAtEndBoundingBox;
export type BoundingBox = NodeBoundingBox | SpecialBoundingBox;

/**
 * Information about a bounding box that has been resolved from a pointer position.
 */
export interface BoundingBoxHit<T extends BoundingBox> {
  boundingBox: T;
  /**
   * The position of the pointer relative to the bounding box. (0, 0) is the top-left corner of the
   * bounding box.
   */
  relativePointerX: number;
  relativePointerY: number;
}

/**
 * Information content model point resolved from a pointer position.
 */
export interface ResolvedPointDetails {
  affinity: SelectionAffinity;
  cMPoint: Point;
  type: 'point';
  lineEdge?: LinePointPosition;
}

interface BoundingBoxCacheEntry {
  boundingBoxes: BoundingBox[];
  options: GetBoundingBoxesOptions;
  rMElementPath: Path;
}

interface GetBoundingBoxesOptions {
  /**
   * Whether to return bounding boxes for child nodes that cannot be selected.
   * @default true
   */
  includeUnselectable?: boolean;
  isFooterOpen?: boolean;
  isHeaderOpen?: boolean;
}

const BOUNDING_BOX_CACHE = new WeakMap<RenderModelElement, Set<BoundingBoxCacheEntry>>();

export const boundingBoxQueries = {
  /**
   * Get the bounding boxes of a render model element's children relative to the top-left corner of
   * the parent element.
   */
  getRMBoundingBoxes(
    editor: Editor,
    rMElementEntry: NodeEntry<RenderModelElement>,
    options: GetBoundingBoxesOptions = {}
  ): BoundingBox[] {
    const [rMElement, rMElementPath] = rMElementEntry;
    const { includeUnselectable = true } = options;

    let cacheEntries = BOUNDING_BOX_CACHE.get(rMElement);

    if (!cacheEntries) {
      cacheEntries = new Set();
      BOUNDING_BOX_CACHE.set(rMElement, cacheEntries);
    }

    const matchingCacheEntry = cacheEntries
      ?.values()
      .find(
        cacheEntry =>
          Path.equals(rMElementPath, cacheEntry.rMElementPath) &&
          equals(options, cacheEntry.options)
      );

    if (matchingCacheEntry) return matchingCacheEntry.boundingBoxes;

    const { offsetX: parentOffsetX, offsetY: parentOffsetY } = Queries.getRMContentOffsetPx(
      editor,
      rMElementEntry
    );

    const direction = Queries.getRMChildrenDirection(rMElement);

    // Only one of these will be used, but it's simpler to track them separately
    let accumulatedWidth = 0;
    let accumulatedHeight = 0;

    const childrenBoundingBoxes = rMElement.children.flatMap((rMNode, index): BoundingBox[] => {
      const rMNodePath = [...rMElementPath, index];

      const { offsetX: childOffsetX, offsetY: childOffsetY } = Queries.getRMSelfOffsetPx(editor, [
        rMNode,
        rMNodePath,
      ]);

      const topPx = parentOffsetY + accumulatedHeight + childOffsetY;
      const leftPx = parentOffsetX + accumulatedWidth + childOffsetX;

      const widthPx = Queries.getRMNodeWidthPx(editor, [rMNode, rMNodePath]);
      const heightPx = Queries.getRMNodeHeightPx(editor, [rMNode, rMNodePath]);

      if (direction === 'horizontal') {
        accumulatedWidth += widthPx;
      } else {
        accumulatedHeight += heightPx;
      }

      if (!includeUnselectable && isRenderModelElement(rMNode) && !editor.isSelectable(rMNode))
        return [];

      return [
        {
          heightPx,
          leftPx,
          rMNodeEntry: [rMNode, rMNodePath],
          topPx,
          type: 'node',
          widthPx,
        },
      ];
    });

    const specialBoundingBoxes = Queries.getRMSpecialBoundingBoxes(editor, rMElementEntry, options);
    const boundingBoxes: BoundingBox[] = [...childrenBoundingBoxes, ...specialBoundingBoxes];

    cacheEntries.add({
      boundingBoxes,
      options,
      rMElementPath,
    });

    return boundingBoxes;
  },

  /**
   * Determine whether a render model element's children should be arranged horizontally or
   * vertically. This is used by getRMBoundingBoxes to determine the position at which each child
   * should be rendered.
   */
  getRMChildrenDirection(rMElement: RenderModelElement): 'horizontal' | 'vertical' {
    if (isAlineInlineElement(rMElement)) return 'horizontal';

    switch (rMElement.type) {
      case 'table-row':
      case 'line':
        return 'horizontal';

      default:
        return 'vertical';
    }
  },

  /**
   * For a given render model element, get the amount by which this element's content should be
   * offset from this element's top-left corner as a result of this element's properties
   * (parent-driven offset).
   *
   * To define offset resulting from the child node's properties (child-driven offset), use
   * getRMSelfOffsetPx instead.

   * This is used by getRMBoundingBoxes to determine the position at which each child should be
   * rendered.
   */
  getRMContentOffsetPx(
    editor: Editor,
    [rMElement, rMElementPath]: NodeEntry<RenderModelElement>
  ): { offsetX: number; offsetY: number } {
    switch (rMElement.type) {
      case 'page':
      case HeaderType.Default:
      case HeaderType.Even:
      case HeaderType.FirstPage:
      case FooterType.Default:
      case FooterType.Even:
      case FooterType.FirstPage: {
        const activePageDimensions = Queries.activePageDimensions(editor, 'in');
        const rMDoc = Queries.getRenderModelDoc(editor);

        const offsetX = Queries.offsetLeftPx({ activePageDimensions });
        const offsetY = Queries.offsetTopPx({
          activePageDimensions,
          rMContainerNode: rMElement,
          rMDoc,
        });

        return { offsetX, offsetY };
      }

      case 'paragraph':
        return {
          offsetX: 0,
          offsetY: Queries.renderedSpacingBeforePx(rMElement) + rMElement.borderLayoutTopPx,
        };

      case 'table': {
        const maxBorderTop = Queries.getTableMaximumBorderWidth(rMElement, 'top', 'px');
        const maxBorderLeft = Queries.getTableMaximumBorderWidth(rMElement, 'left', 'px');
        return { offsetX: maxBorderLeft / 2, offsetY: maxBorderTop / 2 };
      }

      case 'table-cell': {
        const rMTableEntry = Queries.renderModelAbove(editor, {
          at: rMElementPath,
          match: isRenderModelTable,
        });
        if (!rMTableEntry) throw new Error('Expected render model table');
        const [rMTable] = rMTableEntry;

        const offsetX = Queries.renderedTableCellMargin(rMTable, rMElement, 'left', 'px');
        const offsetY = Queries.renderedTableCellMargin(rMTable, rMElement, 'top', 'px');
        return { offsetX, offsetY };
      }

      default:
        return { offsetX: 0, offsetY: 0 };
    }
  },

  /**
   * Get the position of a render model node relative to the top-left corner of the page.
   */
  getRMNodeAbsolutePosition(
    editor: Editor,
    rMTargetEntry: NodeEntry<RenderModelDescendant>,
    unit: Length.Unit = 'px'
  ): {
    xLeft: number;
    xMiddle: number;
    xRight: number;
    yBottom: number;
    yMiddle: number;
    yTop: number;
  } {
    const [, rMTargetPath] = rMTargetEntry;

    const rMPageEntry = Queries.getPageForRMPath(editor, rMTargetPath);
    if (!rMPageEntry)
      throw new Error(
        `Cannot get the absolute position of [${rMTargetPath}] since it does not belong to a page.`
      );

    let rMNodeEntry: NodeEntry<RenderModelNode> = rMPageEntry;
    let xLeft = 0;
    let yTop = 0;

    /**
     * Descend through the bounding box tree and accumulate the top and left offsets of all ancestor
     * bounding boxes of the target.
     */
    while (!Path.equals(rMNodeEntry[1], rMTargetPath)) {
      if (!isRenderModelElementEntry(rMNodeEntry)) break;
      const parentBoundingBoxes = Queries.getRMBoundingBoxes(editor, rMNodeEntry);

      const childBoundingBox = parentBoundingBoxes.find(
        (boundingBox): boundingBox is NodeBoundingBox =>
          boundingBox.type === 'node' &&
          (Path.isAncestor(boundingBox.rMNodeEntry[1], rMTargetPath) ||
            Path.equals(boundingBox.rMNodeEntry[1], rMTargetPath))
      );

      if (!childBoundingBox)
        throw new Error(
          `Cannot find an ancestor bounding box of [${rMTargetPath}] in the bounding boxes of [${rMNodeEntry[1]}].`
        );

      rMNodeEntry = childBoundingBox.rMNodeEntry;
      xLeft += childBoundingBox.leftPx;
      yTop += childBoundingBox.topPx;
    }

    const widthPx = Queries.getRMNodeWidthPx(editor, rMTargetEntry);
    const heightPx = Queries.getRMNodeHeightPx(editor, rMTargetEntry);

    return mapObjectValues(
      {
        xLeft,
        xMiddle: xLeft + widthPx / 2,
        xRight: xLeft + widthPx,
        yBottom: yTop + heightPx,
        yMiddle: yTop + heightPx / 2,
        yTop,
      },
      distance => convertUnit(distance, 'px', unit)
    );
  },

  getRMNodeHeightPx(
    editor: Editor,
    [rMNode, rMNodePath]: NodeEntry<RenderModelDescendant>
  ): number {
    if ('heightPx' in rMNode) return rMNode.heightPx;

    if (isAlineInlineElement(rMNode) || !isRenderModelElement(rMNode)) {
      const rMLineEntry = Queries.renderModelAbove(editor, {
        at: rMNodePath,
        match: isRenderModelLine,
      });
      if (!rMLineEntry) return 0;
      const [rMLine] = rMLineEntry;
      return rMLine.heightPx;
    }

    const { type } = rMNode;

    switch (type) {
      case 'document':
      case 'page':
      case 'headers':
      case 'footers':
      case 'list':
        console.warn(`Unexpected node type passed to getRMNodeHeightPx: ${type}`);
        return 0;

      default:
        assertNeverSafely(type);
        console.warn(`Unhandled node type passed to getRMNodeHeightPx: ${type}`);
        return 0;
    }
  },

  getRMNodeWidthPx(editor: Editor, [rMNode, rMNodePath]: NodeEntry<RenderModelDescendant>): number {
    // For lines inside justified paragraphs, use the width of the paragraph
    if (isRenderModelLine(rMNode)) {
      const rMParagraphEntry = Queries.renderModelAbove(editor, {
        at: rMNodePath,
        match: isRenderModelParagraph,
      });
      if (!rMParagraphEntry) throw new Error('Expected render model paragraph');
      const [rMParagraph] = rMParagraphEntry;
      if (Queries.renderedParagraphAlignment(rMParagraph) === 'justify') return rMParagraph.widthPx;
    }

    if ('widthPx' in rMNode) return rMNode.widthPx;

    if (!isRenderModelElement(rMNode)) {
      const rMLineEntry = Queries.renderModelAbove(editor, {
        at: rMNodePath,
        match: isRenderModelLine,
      });
      if (!rMLineEntry) return 0;
      const [rMLine, rMLinePath] = rMLineEntry;

      const [, lastTextRelativePath] = Node.last(rMLine, []);
      const textRelativePath = Path.relative(rMNodePath, rMLinePath);

      return Queries.getTextWidthPx(editor, {
        isLastNodeInLine: Path.equals(textRelativePath, lastTextRelativePath),
        rMLine,
        textNode: rMNode,
      });
    }

    if (isSignatureFieldElement(rMNode))
      return Queries.getSignatureImageDimensions(rMNode).imgWidthPx;

    if (isBlockInstructionFieldElement(rMNode))
      return Queries.getRMContainerContentWidthPx(editor, { rMPath: rMNodePath });

    const { type } = rMNode;

    switch (type) {
      // Nodes that cannot appear a descendant of a container (page, header or footer)
      case 'document':
      case 'page':
      case 'headers':
      case 'footers':
      case HeaderType.Default:
      case HeaderType.FirstPage:
      case HeaderType.Even:
      case FooterType.Default:
      case FooterType.FirstPage:
      case FooterType.Even:
        console.warn(`Unexpected node type passed to getRMNodeWidthPx: ${type}`);
        return 0;

      case 'link':
      case 'INPUT':
      case 'CANNED':
      case 'dynamic-inline':
      case 'instructionField': // Inline instruction field
        return rMNode.children.reduce(
          (totalWidth, rMChildNode, index) =>
            totalWidth + Queries.getRMNodeWidthPx(editor, [rMChildNode, [...rMNodePath, index]]),
          0
        );

      case 'drawing':
        return Queries.getDrawingDimensions(rMNode).containerWidthPx;

      case 'tab':
        return rMNode.tabWidthPx;

      case 'break':
        return Queries.renderedBreakWidth(editor, [rMNode, rMNodePath], 'px');

      case 'bookmarkStart':
      case 'bookmarkEnd':
      case 'list':
      case 'suggestionMarkerStart':
      case 'suggestionMarkerEnd':
        return 0;
      case 'checkbox':
        return Queries.renderedCheckboxSizePx(rMNode.children.at(0) ?? {});

      default:
        assertNeverSafely(type);
        console.warn(`Unhandled node type passed to getRMNodeWidthPx: ${type}`);
        return 0;
    }
  },

  /**
   * For a given render model node, get the amount by which this node should be offset from its
   * parent element as a direct result of this node's properties (child-driven offset).
   *
   * To define offset resulting from the parent element's properties (parent-driven offset), use
   * getRMContentOffsetPx instead.
   *
   * This is used by getRMBoundingBoxes to determine the position at which a node should be rendered
   * relative to its parent.
   */
  getRMSelfOffsetPx(
    editor: Editor,
    [rMNode, rMNodePath]: NodeEntry<RenderModelDescendant>
  ): { offsetX: number; offsetY: number } {
    if (!isRenderModelElement(rMNode)) return { offsetX: 0, offsetY: 0 };

    switch (rMNode.type) {
      case 'paragraph':
        return { offsetX: Queries.renderedParagraphLeftIndent(rMNode, 'px'), offsetY: 0 };

      case 'line': {
        const rMParagraphEntry = Queries.renderModelAbove(editor, {
          at: rMNodePath,
          match: isRenderModelParagraph,
        });
        if (!rMParagraphEntry) throw new Error('Expected render model paragraph');
        const [paraProps] = rMParagraphEntry;

        return {
          offsetX: Queries.lineOffsetLeftPx(editor, { paraProps, rMLine: rMNode }).leftOffsetPx,
          offsetY: 0,
        };
      }

      case 'table':
        return { offsetX: rMNode.alignOffsetPx, offsetY: 0 };

      default:
        return { offsetX: 0, offsetY: 0 };
    }
  },

  /**
   * Get any special (not based on children) bounding boxes of a given render model element.
   */
  getRMSpecialBoundingBoxes<T extends RenderModelElement>(
    editor: Editor,
    [rMElement, rMElementPath]: NodeEntry<T>,
    options: GetBoundingBoxesOptions = {}
  ): BoundingBox[] {
    const { includeUnselectable = true, isFooterOpen, isHeaderOpen } = options;
    const specialBoundingBoxes: BoundingBox[] = [];

    if (isRenderModelPage(rMElement)) {
      const rMDoc = Queries.getRenderModelDoc(editor);
      const activePageDimensions = Queries.activePageDimensions(editor, 'in');
      const widthPx = inToPx(activePageDimensions.width);
      const heightPx = inToPx(activePageDimensions.height);

      const rMHeaderEntry = Queries.getRMHeaderOrFooterEntryOnPage(editor, {
        pageNumber: rMElement.pageNum,
        rMDocProps: rMDoc,
        type: 'header',
      });

      const headerHeightPx = Queries.renderedHeaderOrFooterHeight(
        {
          activePageDimensions,
          rMElement: rMHeaderEntry[0],
          rMPart: rMDoc.headers,
        },
        'px'
      );

      const rMFooterEntry = Queries.getRMHeaderOrFooterEntryOnPage(editor, {
        pageNumber: rMElement.pageNum,
        rMDocProps: rMDoc,
        type: 'footer',
      });

      const footerHeightPx = Queries.renderedHeaderOrFooterHeight(
        {
          activePageDimensions,
          rMElement: rMFooterEntry[0],
          rMPart: rMDoc.footers,
        },
        'px'
      );

      // Include the header as if it were a child of the page
      if (includeUnselectable || isHeaderOpen)
        specialBoundingBoxes.push({
          directHitOnly: true,
          heightPx: headerHeightPx,
          leftPx: 0,
          rMNodeEntry: rMHeaderEntry,
          topPx: 0,
          type: 'node',
          widthPx,
        });

      // Include the footer as if it were a child of the page
      if (includeUnselectable || isFooterOpen)
        specialBoundingBoxes.push({
          directHitOnly: true,
          heightPx: footerHeightPx,
          leftPx: 0,
          rMNodeEntry: rMFooterEntry,
          topPx: heightPx - footerHeightPx,
          type: 'node',
          widthPx,
        });

      /**
       * If this is the last page in the document and the last block is a non-paragraph, create a
       * bounding box below the last block that will create a new empty paragraph when clicked.
       */
      const lastElement = rMElement.children.at(-1);
      if (
        rMElement.pageNum === rMDoc.children.length &&
        !(lastElement && isRenderModelParagraph(lastElement))
      ) {
        const totalChildrenHeightPx = sumBy(rMElement.children, (child, index) =>
          Queries.getRMNodeHeightPx(editor, [child, rMElementPath.concat(index)])
        );

        const startY = headerHeightPx + totalChildrenHeightPx;
        const endY = heightPx - footerHeightPx;

        specialBoundingBoxes.push({
          directHitOnly: true,
          heightPx: endY - startY,
          leftPx: 0,
          topPx: startY,
          type: 'ensure-paragraph-at-end',
          widthPx,
        });
      }
    }

    // Paragraph list markers
    if (isRenderModelParagraph(rMElement) && rMElement.listMarker) {
      const [rMLine] = rMElement.children;

      const {
        leftOffsetPx: lineLeftOffsetPx,
        listMarkerBufferPx,
        listMarkerTextWidthPx,
      } = Queries.lineOffsetLeftPx(editor, {
        paraProps: rMElement,
        rMLine,
      });
      const isBufferPos = Math.sign(listMarkerBufferPx) === 1;

      const isEndAligned =
        Queries.activeListLevelAlignment(rMElement.listMarker.listLevelDefinition) ===
        ListLevelAlignment.End;

      specialBoundingBoxes.push({
        directHitOnly: true,
        heightPx: rMLine.heightPx,
        leftPx:
          isBufferPos || isEndAligned
            ? lineLeftOffsetPx - Math.abs(listMarkerBufferPx) - listMarkerTextWidthPx
            : lineLeftOffsetPx - Math.abs(listMarkerBufferPx),
        rMParagraphEntry: [rMElement, rMElementPath],
        topPx: Queries.renderedSpacingBeforePx(rMElement) + rMElement.borderLayoutTopPx,
        type: 'list-marker',
        widthPx: listMarkerTextWidthPx,
      });
    }

    return specialBoundingBoxes;
  },

  /**
   * Which grid column of a table a pointer is over, or null when it is not over a table.
   * For a horizontally merged cell it reports the sub-column the cursor is physically over.
   */
  getTableGridColumnAtPointer(
    editor: Editor,
    options: { pointerX: number; pointerY: number; rMPageEntry: NodeEntry<RenderModelPage> }
  ): number | null {
    const { pointerX, pointerY, rMPageEntry } = options;

    /**
     * Descend the bounding-box tree, subtracting each box's offset so the pointer stays relative to
     * the current element, and remember the innermost table entered (nested tables resolve to the
     * inner table, matching where the pointer physically is).
     */
    let currentEntry: NodeEntry<RenderModelElement> = rMPageEntry;
    let relativeX = pointerX;
    let relativeY = pointerY;
    let innermostTable: { entry: NodeEntry<RenderModelTable>; x: number; y: number } | null = null;

    let childBoundingBox = findChildBoundingBoxAtPointer(
      editor,
      currentEntry,
      relativeX,
      relativeY
    );
    while (childBoundingBox) {
      const childX = relativeX - childBoundingBox.leftPx;
      const childY = relativeY - childBoundingBox.topPx;

      if (isRenderModelTableBoundingBox(childBoundingBox))
        innermostTable = { entry: childBoundingBox.rMNodeEntry, x: childX, y: childY };

      // A leaf (e.g. a text node) is as deep as the descent goes.
      if (!isRenderModelElementEntry(childBoundingBox.rMNodeEntry)) break;

      currentEntry = childBoundingBox.rMNodeEntry;
      relativeX = childX;
      relativeY = childY;
      childBoundingBox = findChildBoundingBoxAtPointer(editor, currentEntry, relativeX, relativeY);
    }

    if (!innermostTable) return null;
    const { entry: rMTableEntry, x: tableX, y: tableY } = innermostTable;
    const [rMTable] = rMTableEntry;

    /**
     * Find the row under the pointer only to locate the cell beneath it for column bucketing; the
     * row is not part of the result, so a hit anywhere in a vertically merged cell resolves the same
     * column regardless of which of its rows the pointer is over.
     */
    const rowBoundingBoxes = Queries.getRMBoundingBoxes(editor, rMTableEntry).filter(
      isRenderModelTableRowBoundingBox
    );
    const rowBoundingBox =
      rowBoundingBoxes.find(({ heightPx, topPx }) => tableY <= Math.round(topPx + heightPx)) ??
      rowBoundingBoxes.at(-1);
    if (!rowBoundingBox) return null;

    const rowRelativeX = tableX - rowBoundingBox.leftPx;
    const cellBoundingBoxes = Queries.getRMBoundingBoxes(editor, rowBoundingBox.rMNodeEntry).filter(
      isRenderModelTableCellBoundingBox
    );
    const cellBoundingBox =
      cellBoundingBoxes.find(
        ({ leftPx, widthPx }) => rowRelativeX <= Math.round(leftPx + widthPx)
      ) ?? cellBoundingBoxes.at(-1);
    if (!cellBoundingBox) return null;

    const [{ endColumnIndex, startColumnIndex }] = cellBoundingBox.rMNodeEntry;
    if (startColumnIndex === endColumnIndex) return startColumnIndex;

    /**
     * The pointer is inside a horizontally merged cell: walk its grid columns left to right,
     * accumulating their pixel widths, to find which one the pointer is over.
     */
    const cellRelativeX = rowRelativeX - cellBoundingBox.leftPx;
    let columnRightPx = 0;
    for (let columnIndex = startColumnIndex; columnIndex < endColumnIndex; columnIndex++) {
      columnRightPx += rMTable.columnWidthsPx[columnIndex] ?? 0;
      if (cellRelativeX < columnRightPx) return columnIndex;
    }

    return endColumnIndex;
  },

  /**
   * Get the point or clickable object for a given pointer location.
   *
   * Clickable objects include things like list markers, which can be distinguished from points by
   * checking the type property of this function's return value.
   *
   * If you don't care about clickable objects, use resolvePointerToPoint instead.
   */
  resolvePointerToClickable(
    editor: Editor,
    options: {
      isFooterOpen: boolean;
      isHeaderOpen: boolean;
      pointerX: number;
      pointerY: number;
      rMElementEntry: NodeEntry<RenderModelElement>;
    }
  ): ResolvedPointDetails | SpecialBoundingBox | null {
    const boundingBoxHit = resolvePointerToBoundingBox(editor, options);
    if (!boundingBoxHit) return null;

    const { boundingBox, relativePointerX } = boundingBoxHit;

    if (boundingBox.type === 'node')
      return resolveNodeBoundingBoxHitToPoint(editor, {
        boundingBox,
        relativePointerX,
      });

    return boundingBox;
  },

  /**
   * Get the Slate point for a given pointer location, excluding clickable objects such as list
   * markers.
   *
   * This means that if the pointer is over a clickable object, that object will be ignored. If you
   * care about clickable objects, use resolvePointerToClickable instead.
   */
  resolvePointerToPoint(
    editor: Editor,
    options: {
      isFooterOpen: boolean;
      isHeaderOpen: boolean;
      pointerX: number;
      pointerY: number;
      rMElementEntry: NodeEntry<RenderModelElement>;
    }
  ): ResolvedPointDetails | null {
    const boundingBoxHit = resolvePointerToBoundingBox<NodeBoundingBox>(editor, {
      ...options,
      match: boundingBox => boundingBox.type === 'node',
    });
    if (!boundingBoxHit) return null;

    return resolveNodeBoundingBoxHitToPoint(editor, boundingBoxHit);
  },
};

type MatchBoundingBox<T extends BoundingBox> = (boundingBox: BoundingBox) => boundingBox is T;

type ResolvePointerToBoundingBoxOptions<T extends BoundingBox> = {
  isFooterOpen: boolean;
  isHeaderOpen: boolean;
  /** The position of the pointer relative to the top-left corner of the element. */
  pointerX: number;
  pointerY: number;
  rMElementEntry: NodeEntry<RenderModelElement>;
} &
  // Make the match option required if a type argument was passed
  (BoundingBox extends T ? { match?: MatchBoundingBox<T> } : { match: MatchBoundingBox<T> });

/**
 * Recursive algorithm that gets the bounding box corresponding to a pointer position within an
 * element. A match option can be passed to inlcude only certain types of bounding box.
 */
function resolvePointerToBoundingBox<T extends BoundingBox = BoundingBox>(
  editor: Editor,
  options: ResolvePointerToBoundingBoxOptions<T>
): BoundingBoxHit<T> | null {
  const {
    isFooterOpen,
    isHeaderOpen,
    match = (_boundingBox: BoundingBox): _boundingBox is T => true,
    pointerX,
    pointerY,
    rMElementEntry,
  } = options;
  const [rMElement] = rMElementEntry;

  const childrenDirection = Queries.getRMChildrenDirection(rMElement);
  const boundingBoxes: T[] = Queries.getRMBoundingBoxes(editor, rMElementEntry, {
    includeUnselectable: false,
    isFooterOpen,
    isHeaderOpen,
  }).filter(match);

  function handleHit(boundingBoxIndex: number): BoundingBoxHit<T> | null {
    const boundingBox = boundingBoxes.at(boundingBoxIndex);
    if (!boundingBox) return null;

    const result: BoundingBoxHit<T> = {
      boundingBox,
      relativePointerX: pointerX - boundingBox.leftPx,
      relativePointerY: pointerY - boundingBox.topPx,
    };

    if (boundingBox.type === 'node' && isRenderModelElementEntry(boundingBox.rMNodeEntry)) {
      /**
       * If the hit is on a non-first vertically merged cell, return early rather than descending
       * into it. This will be caught at the table level using the logic below.
       */
      if (
        isRenderModelTableCellBoundingBox(boundingBox) &&
        !boundingBox.rMNodeEntry[0].verticalMergeIsStart
      )
        return result;

      // Otherwise, recurse into that element
      const recursiveResult = resolvePointerToBoundingBox(editor, {
        ...options,
        pointerX: result.relativePointerX,
        pointerY: result.relativePointerY,
        rMElementEntry: boundingBox.rMNodeEntry,
      });

      /**
       * If we're at the table level and the recusive result was a non-first vertically merged cell,
       * try again using the previous table row. This should eventually result in us resolving a hit
       * in the first vertically merged cell.
       */
      if (
        recursiveResult &&
        isRenderModelTable(rMElement) &&
        isRenderModelTableCellBoundingBox(recursiveResult.boundingBox) &&
        !recursiveResult.boundingBox.rMNodeEntry[0].verticalMergeIsStart &&
        boundingBoxIndex > 0
      )
        return handleHit(boundingBoxIndex - 1);

      return recursiveResult ?? result;
    }

    return result;
  }

  // Track the closest bounding box to the pointer, using the rules defined in isCloser
  let closestIndex: number | null = null;
  let closestMainAxisDistance = Infinity;
  let closestCrossAxisDistance = Infinity;

  function isCloser(mainAxisDistance: number, crossAxisDistance: number, isAfterMainAxis: boolean) {
    // If the main axis distances are tied, compare the cross axis
    if (mainAxisDistance === closestMainAxisDistance) {
      /**
       * If the cross axis distances are also tied, choose the newer bounding box if and only if the
       * pointer was after this bounding box in the main axis. Otherwise, keep the previous bounding
       * box. This is to ensure that zero-width and zero-height bounding boxes can still be
       * selected.
       */
      if (crossAxisDistance === closestCrossAxisDistance) return isAfterMainAxis;

      return crossAxisDistance < closestCrossAxisDistance;
    }

    // Always prefer a closer main axis distance, regardless of the cross axis
    return mainAxisDistance < closestMainAxisDistance;
  }

  for (const [boundingBoxIndex, boundingBox] of arrayEntries(boundingBoxes)) {
    const { directHitOnly, heightPx, leftPx, topPx, widthPx } = boundingBox;

    /**
     * Round the bounding box values to the nearest pixel so that floating point errors don't affect
     * the comparison of values that should be identical.
     */
    const roundedLeftPx = Math.round(leftPx);
    const roundedTopPx = Math.round(topPx);
    const roundedRightPx = Math.round(leftPx + widthPx);
    const roundedBottomPx = Math.round(topPx + heightPx);
    const roundedWidthPx = roundedRightPx - roundedLeftPx;
    const roundedHeightPx = roundedBottomPx - roundedTopPx;

    const [mainAxisStart, mainAxisSize, mainAxisPointer] = {
      horizontal: [roundedLeftPx, roundedWidthPx, pointerX],
      vertical: [roundedTopPx, roundedHeightPx, pointerY],
    }[childrenDirection];

    const [crossAxisStart, crossAxisSize, crossAxisPointer] = {
      horizontal: [roundedTopPx, roundedHeightPx, pointerY],
      vertical: [roundedLeftPx, roundedWidthPx, pointerX],
    }[childrenDirection];

    const mainAxisRelative = mainAxisPointer - mainAxisStart;
    const crossAxisRelative = crossAxisPointer - crossAxisStart;

    const isAfterMainAxisStart = mainAxisRelative >= 0;
    const isBeforeMainAxisEnd = mainAxisRelative <= mainAxisSize;
    const isAfterCrossAxisStart = crossAxisRelative >= 0;
    const isBeforeCrossAxisEnd = crossAxisRelative <= crossAxisSize;

    const isInsideMainAxis = isAfterMainAxisStart && isBeforeMainAxisEnd;
    const isInsideCrossAxis = isAfterCrossAxisStart && isBeforeCrossAxisEnd;

    // Immediately return a bounding box that the pointer is inside
    if (isInsideMainAxis && isInsideCrossAxis) return handleHit(boundingBoxIndex);

    /**
     * If the bounding box can only be selected when the pointer is inside it, do not consider it
     * any further.
     */
    if (directHitOnly) continue;

    const mainAxisDistance = isInsideMainAxis
      ? 0
      : isAfterMainAxisStart
        ? mainAxisRelative - mainAxisSize
        : -mainAxisRelative;

    const crossAxisDistance = isInsideCrossAxis
      ? 0
      : isAfterCrossAxisStart
        ? crossAxisRelative - crossAxisSize
        : -crossAxisRelative;

    if (isCloser(mainAxisDistance, crossAxisDistance, isAfterMainAxisStart)) {
      closestIndex = boundingBoxIndex;
      closestMainAxisDistance = mainAxisDistance;
      closestCrossAxisDistance = crossAxisDistance;
    }
  }

  return closestIndex === null ? null : handleHit(closestIndex);
}

export function isRenderModelContentBlockBoundingBox(
  boundingBox: BoundingBox
): boundingBox is NodeBoundingBox<RenderModelContentBlock> {
  return boundingBox.type === 'node' && isRenderModelContentBlock(boundingBox.rMNodeEntry[0]);
}

export function isRenderModelTableBoundingBox(
  boundingBox: BoundingBox
): boundingBox is NodeBoundingBox<RenderModelTable> {
  return boundingBox.type === 'node' && isRenderModelTable(boundingBox.rMNodeEntry[0]);
}

export function isRenderModelTableRowBoundingBox(
  boundingBox: BoundingBox
): boundingBox is NodeBoundingBox<RenderModelTableRow> {
  return boundingBox.type === 'node' && isRenderModelTableRow(boundingBox.rMNodeEntry[0]);
}

export function isRenderModelTableCellBoundingBox(
  boundingBox: BoundingBox
): boundingBox is NodeBoundingBox<RenderModelTableCell> {
  return boundingBox.type === 'node' && isRenderModelTableCell(boundingBox.rMNodeEntry[0]);
}

export function isRenderModelLineChildBoundingBox(
  editor: Editor,
  boundingBox: BoundingBox
): boundingBox is NodeBoundingBox<RenderModelLineChild> {
  return boundingBox.type === 'node' && isRenderModelLineChild(editor, boundingBox.rMNodeEntry[0]);
}

/** The node bounding box among an element's children that contains the (element-relative) pointer. */
function findChildBoundingBoxAtPointer(
  editor: Editor,
  entry: NodeEntry<RenderModelElement>,
  pointerX: number,
  pointerY: number
): NodeBoundingBox | undefined {
  return Queries.getRMBoundingBoxes(editor, entry).find(
    (boundingBox): boundingBox is NodeBoundingBox =>
      boundingBox.type === 'node' && isPointerInsideBoundingBox(boundingBox, pointerX, pointerY)
  );
}

function isPointerInsideBoundingBox(
  boundingBox: BaseBoundingBox,
  pointerX: number,
  pointerY: number
): boolean {
  const left = Math.round(boundingBox.leftPx);
  const top = Math.round(boundingBox.topPx);
  const right = Math.round(boundingBox.leftPx + boundingBox.widthPx);
  const bottom = Math.round(boundingBox.topPx + boundingBox.heightPx);
  return pointerX >= left && pointerX <= right && pointerY >= top && pointerY <= bottom;
}

/**
 * Get the Slate point corresponding to a bounding box hit on a node.
 */
function resolveNodeBoundingBoxHitToPoint(
  editor: Editor,
  boundingBoxHit: Pick<BoundingBoxHit<NodeBoundingBox>, 'boundingBox' | 'relativePointerX'>
): ResolvedPointDetails {
  const {
    boundingBox: { rMNodeEntry },
    relativePointerX,
  } = boundingBoxHit;
  const [, rMPath] = rMNodeEntry;

  const rMVoidEntry = Queries.renderModelAbove(editor, {
    at: rMPath,
    match: node => Element.isElement(node) && Editor.isVoid(editor, node),
  });

  if (isRenderModelTextEntry(rMNodeEntry) && !rMVoidEntry) {
    const resolvedPointDetails = getTextNodePointForXPosition(editor, rMNodeEntry, {
      xPosition: relativePointerX,
    });
    const adjustedPoint = Queries.getSelectablePoint(editor, {
      point: resolvedPointDetails.cMPoint,
    });
    return { ...resolvedPointDetails, cMPoint: adjustedPoint };
  }

  /**
   * If a non-text node was returned or the text node is inside a void element, return the end of
   * the node. For example, this can happen when clicking in an empty split table cell. In the case
   * of void elements, this ensures that a valid content model point will be selected when the
   * render model text differs from the content model text (such as in instruction field elements).
   */
  const cMPath = Queries.getCMPathFromRMPath(editor, rMVoidEntry?.[1] ?? rMPath);

  return {
    affinity: 'forward',
    cMPoint: Queries.getSelectablePoint(editor, { point: Editor.end(editor, cMPath) }),
    type: 'point',
  };
}

/**
 * Get the Slate point corresponding to an x position relative to a given text node.
 */
function getTextNodePointForXPosition(
  editor: Editor,
  [rMText, rMPath]: NodeEntry<RenderModelText>,
  options: { xPosition: number }
): ResolvedPointDetails {
  const { xPosition } = options;
  const {
    relativeStartPoint: { offset: startOffset },
    text,
  } = rMText;
  const cMPath = Queries.getCMPathFromRMPath(editor, rMPath);

  const rMLineEntry = Queries.renderModelAbove(editor, {
    at: rMPath,
    match: isRenderModelLine,
  });
  if (!rMLineEntry) throw new Error('Expected render model line');
  const [rMLine, rMLinePath] = rMLineEntry;

  const [rMLineStart, rMLineEnd] = Queries.renderModelEdges(editor, rMLinePath);

  const isLastNodeInLine = Path.equals(rMPath, rMLineEnd.path);

  /** The width of the text node in the previous iteration of the for loop. */
  let previousWidth = 0;

  /**
   * The offset of the pointer relative to the render model text node. The default value of
   * text.length is only used if the for loop exits without overriding it.
   */
  let relativeOffset = text.length;

  /**
   * Determine the number of characters at which the last character's midpoint is to the left of the
   * pointer.
   */
  for (let i = 0; i < text.length; i++) {
    // A copy of the text node up to and including the i-th character
    const partialTextNode: RenderModelText = {
      ...rMText,
      text: text.slice(0, i + 1),
    };

    const textWidth = Queries.getTextWidthPx(editor, {
      isLastNodeInLine,
      rMLine,
      textNode: partialTextNode,
    });

    const ithCharWidth = textWidth - previousWidth;
    const ithCharMiddle = previousWidth + ithCharWidth / 2;

    if (xPosition < ithCharMiddle) {
      relativeOffset = i;
      break;
    }

    previousWidth = textWidth;
  }

  const rMPoint = { offset: relativeOffset, path: rMPath };
  const cMPoint = { offset: startOffset + relativeOffset, path: cMPath };

  const isLineStart = Point.equals(rMPoint, rMLineStart);
  const isLineEnd = Point.equals(rMPoint, rMLineEnd);

  // If no character's midpoint was left of the pointer, return the end of the text node
  return {
    affinity: isLineEnd ? 'backward' : 'forward',
    cMPoint,
    lineEdge: isLineStart ? 'start' : isLineEnd ? 'end' : 'within',
    type: 'point',
  };
}
